/**
 * TinyClub Color System
 * Platform-agnostic color definitions for shared use across mobile and web
 */

// Core brand colors - static values that don't change with theme
export const coreColors = {
  deepSlate: "#17262D",
  yellow: "#FCEB6E",
  olive: "#596F46",
  blue: "#1F4AB3",
  blueBright: "#387AFE",
  red: "#EE472C",
  offWhite: "#FAFAF5",
  offWhiteDark: "#FDFDF3",
} as const;

// Named color definitions for light theme
export const lightColors = {
  background: "#FAFAF5",
  textPrimary: "#17262D",
  textSecondary: "#17262DCC", // 80% opacity
  textTertiary: "#17262D88", // 50% opacity
  textDisabled: "#17262D4D", // 30% opacity
  textReversed: "#FAFAF5",
  borderPrimary: "#17262D",
  borderSecondary: "#17262D66", // 40% opacity
  borderTertiary: "#17262D1A", // 10% opacity
  borderSubtle: "#17262D0D", // 5% opacity
  surface: "#17262D05", // 2% opacity
  surface1: "#17262D0A", // 4% opacity
  surface2: "#17262D0F", // 6% opacity
  surface3: "#17262D1E", // 12% opacity
  surfaceBlur: "#FFFFFF33", // 20% opacity
  accentPrimary: "#FCEB6E",
  accentPrimaryText: "#17262D",
  accentSecondary: "#1F4AB3",
  accentSecondaryText: "#FAFAF5",
  accentPositive: "#596F46",
  accentNeutral: "#17262DCC", // 80% opacity
  accentNegative: "#EE472C",
  buttonSurface: "#17262D05", // 2% opacity
  buttonText: "#17262D",
  buttonBorder: "#17262D33", // 20% opacity
  textLight: "#FAFAF5",
  textDark: "#17262D",
  inputBackground: "#F1F2ED",
} as const;

// Named color definitions for dark theme
export const darkColors = {
  background: "#17262D",
  textPrimary: "#FDFDF3",
  textSecondary: "#FDFDF3CC", // 80% opacity
  textTertiary: "#FDFDF388", // 50% opacity
  textDisabled: "#FDFDF388", // 50% opacity
  textReversed: "#17262D",
  borderPrimary: "#FDFDF3",
  borderSecondary: "#FDFDF366", // 40% opacity
  borderTertiary: "#FDFDF31A", // 10% opacity
  borderSubtle: "#FDFDF30D", // 5% opacity
  surface: "#FDFDF305", // 2% opacity
  surface1: "#FDFDF30A", // 4% opacity
  surface2: "#FDFDF30F", // 6% opacity
  surface3: "#FDFDF31E", // 12% opacity
  surfaceBlur: "#00000033", // 20% opacity
  accentPrimary: "#FCEB6E",
  accentPrimaryText: "#17262D",
  accentSecondary: "#387AFE",
  accentSecondaryText: "#FDFDF3",
  accentPositive: "#596F46",
  accentNeutral: "#FDFDF3CC", // 80% opacity
  accentNegative: "#EE472C",
  buttonSurface: "#FDFDF305", // 2% opacity
  buttonText: "#FDFDF3",
  buttonBorder: "#FDFDF333", // 20% opacity
  textLight: "#FDFDF3",
  textDark: "#17262D",
  inputBackground: "#1b2a30",
} as const;

// Type for core colors (literal types preserved for brand consistency)
export type CoreColors = typeof coreColors;

// Type for named colors (string types to allow theme variations)
export interface NamedColors {
  background: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  textReversed: string;
  borderPrimary: string;
  borderSecondary: string;
  borderTertiary: string;
  borderSubtle: string;
  surface: string;
  surface1: string;
  surface2: string;
  surface3: string;
  surfaceBlur: string;
  accentPrimary: string;
  accentPrimaryText: string;
  accentSecondary: string;
  accentSecondaryText: string;
  accentPositive: string;
  accentNeutral: string;
  accentNegative: string;
  buttonSurface: string;
  buttonText: string;
  buttonBorder: string;
  textLight: string;
  textDark: string;
  inputBackground: string;
}
